import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Loader2, CheckCircle2, ShieldCheck, Wifi, Database } from 'lucide-react';

/**
 * BankConnectProgress — status splash bridging the "silent gap" after the
 * user finishes Plaid Link (or Mono Widget) but before the app can either
 * (a) open the backlog fee dialog or (b) confirm the sync completed.
 *
 * Phases (drives which step animates as "in progress"):
 *   "exchanging" → POST /api/plaid/exchange-token in flight
 *   "polling"    → syncing transactions from the bank (via Plaid /transactions/sync)
 *   "finishing"  → last step before the backlog dialog or success toast
 *
 * iter1204-plaid-ux: Dismissible in the `polling` / `finishing` phases so
 * the user can close it and continue working — the global
 * `BacklogProgressBanner` (mounted in DashboardLayout) takes over and
 * shows sync progress on every page. This matches the Mono flow's
 * "Close: keep syncing in background" pattern. During `exchanging` the
 * dialog stays blocking because the round-trip is short (< 3s) and
 * dismissing mid-exchange creates orphan public_tokens.
 */
export function BankConnectProgress({
  open,
  phase = 'exchanging',
  institutionName,
  providerLabel = 'your bank',
  onDismiss,
}) {
  if (!open) return null;

  const steps = [
    {
      key: 'exchanging',
      icon: ShieldCheck,
      title: 'Securing the connection',
      desc: 'Handshaking with your bank so we can pull transactions.',
    },
    {
      key: 'polling',
      icon: Wifi,
      title: 'Fetching your transaction history',
      desc: 'Banks sometimes take 5-15 seconds to warm up the feed. Hang tight.',
    },
    {
      key: 'finishing',
      icon: Database,
      title: 'Preparing your bank feed',
      desc: 'Building the ledger view and checking for historical transactions.',
    },
  ];

  const currentIndex = Math.max(0, steps.findIndex((s) => s.key === phase));
  // iter1204-plaid-ux: exchanging stays blocking, later phases are dismissible.
  const isDismissible = phase !== 'exchanging' && typeof onDismiss === 'function';

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o && isDismissible) onDismiss();
      }}
    >
      <DialogContent
        className={`sm:max-w-md ${isDismissible ? '' : "[&>button[type='button']]:hidden"}`}
        onEscapeKeyDown={(e) => { if (!isDismissible) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (!isDismissible) e.preventDefault(); }}
        onInteractOutside={(e) => { if (!isDismissible) e.preventDefault(); }}
        data-testid="bank-connect-progress-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Loader2 className="w-5 h-5 text-[#108981] animate-spin" />
            Connecting {institutionName || providerLabel}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {steps.map((step, idx) => {
            const Icon = step.icon;
            const isDone = idx < currentIndex;
            const isActive = idx === currentIndex;
            const isPending = idx > currentIndex;

            return (
              <div
                key={step.key}
                className={`flex items-start gap-3 p-3 rounded-lg border transition-colors ${
                  isActive
                    ? 'border-[#108981]/40 bg-[#108981]/5'
                    : isDone
                    ? 'border-emerald-200 bg-emerald-50'
                    : 'border-slate-200 bg-slate-50/50'
                }`}
                data-testid={`connect-progress-step-${step.key}`}
                data-state={isDone ? 'done' : isActive ? 'active' : 'pending'}
              >
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                    isActive
                      ? 'bg-[#108981]/15'
                      : isDone
                      ? 'bg-emerald-100'
                      : 'bg-slate-100'
                  }`}
                >
                  {isDone ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  ) : isActive ? (
                    <Loader2 className="w-4 h-4 text-[#108981] animate-spin" />
                  ) : (
                    <Icon className="w-4 h-4 text-slate-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p
                    className={`text-sm font-medium ${
                      isPending ? 'text-slate-400' : 'text-slate-900'
                    }`}
                  >
                    {step.title}
                  </p>
                  <p
                    className={`text-xs mt-0.5 ${
                      isPending ? 'text-slate-400' : 'text-muted-foreground'
                    }`}
                  >
                    {step.desc}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {isDismissible ? (
          <>
            <p
              className="text-xs text-center text-muted-foreground mt-1"
              data-testid="bank-connect-progress-hint"
            >
              You can close this — a progress banner will keep you posted while the sync
              continues in the background.
            </p>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={onDismiss}
                className="w-full"
                data-testid="bank-connect-progress-close-btn"
              >
                Close: keep syncing in background
              </Button>
            </DialogFooter>
          </>
        ) : (
          <p
            className="text-xs text-center text-muted-foreground mt-1"
            data-testid="bank-connect-progress-warning"
          >
            Please don&apos;t close this window or click Connect again — we&apos;re
            finishing up in the background.
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
